import { Route } from "@angular/router";
import { initialDataResolver } from "./app.resolvers";
import { Permission } from "./core/auth/permissions";
import { NoAuthGuard } from "./core/auth/guards/noAuth.guard";
import { AuthCallbackComponent } from "./modules/auth/auth-callback/auth-callback.component";
import { AuthGuard } from "./core/auth/guards/auth.guard";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "home" },

  { path: "auth-callback", component: AuthCallbackComponent },
  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "home" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    loadComponent: () => import("./layout/layout.component").then(v => v.LayoutComponent),
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () => import("./modules/auth/confirmation-required/confirmation-required.routes"),
      },
      {
        path: "forgot-password",
        loadChildren: () => import("./modules/auth/forgot-password/forgot-password.routes"),
      },
      {
        path: "reset-password",
        loadChildren: () => import("./modules/auth/reset-password/reset-password.routes"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("./modules/auth/sign-in/sign-in.routes"),
      },
      {
        path: "sign-up",
        loadChildren: () => import("./modules/auth/sign-up/sign-up.routes"),
      },
      {
        path: "microsoft-auth-callback",
        loadChildren: () => import("./modules/auth/microsoft-sso/microsoft-sso.routes"),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadComponent: () => import("./layout/layout.component").then(v => v.LayoutComponent),
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () => import("./modules/auth/sign-out/sign-out.routes"),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    loadComponent: () => import("./layout/layout.component").then(v => v.LayoutComponent),
    data: {
      layout: "empty",
    },
  },
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    loadComponent: () => import("./layout/layout.component").then(v => v.LayoutComponent),
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "home",
        loadChildren: () => import("./modules/home/home.routes"),
      },
      {
        path: "my-insights",
        loadComponent: () => import("./modules/my-work/my-insights/my-insights.component").then(m => m.MyInsightsComponent),
        data: { permissions: [Permission.viewInsightsPage] },
      },
      {
        path: "materials",
        data: { permissions: [Permission.manageAggSoilMaterialPermission] },
        loadChildren: () => import("./modules/admin/materials/materials.routes"),
      },
      {
        path: "cement-materials",
        data: { permissions: [Permission.manageCementMaterialPermission] },
        loadChildren: () => import("./modules/admin/cement-materials/cement-materials.routes"),
      },
      {
        path: "material-types-and-descriptions",
        data: { permissions: [Permission.manageMaterialTypeDescPermission] },
        loadChildren: () => import("./modules/admin/material-types-and-descriptions/material-types-and-descriptions.routes"),
      },
      {
        path: "cement-client-sources",
        data: { permissions: [Permission.manageCementClientSourcePermission] },
        loadChildren: () => import("./modules/admin/cement-client-sources/cement-client-sources.routes"),
      },
      {
        path: "test-providers",
        data: { permissions: [Permission.manageTestProvidersPermission] },
        loadChildren: () => import("./modules/admin/test-providers/test-providers.routes"),
      },
      {
        path: "external-companies",
        data: { permissions: [Permission.manageExternalProvidersPermission] },
        loadChildren: () => import("./modules/admin/external-companies/external-companies.routes"),
      },
      {
        path: "equipment",
        data: { permissions: [Permission.manageEquipmentsPermission] },
        loadChildren: () => import("./modules/admin/equipment/equipment.routes"),
      },
      {
        path: "scan",
        loadComponent: () => import("./modules/scan/scan-redirect.component").then(m => m.ScanRedirectComponent),
      },
      {
        path: "suppliers",
        data: { permissions: [Permission.manageSuppliersPermission] },
        loadChildren: () => import("./modules/admin/suppliers/suppliers.routes"),
      },
      {
        path: "specifications",
        data: { permissions: [Permission.manageSpecificationsPermission] },
        loadChildren: () => import("./modules/admin/specifications/specifications.routes"),
      },
      {
        path: "work-order-templates",
        data: { permissions: [Permission.manageWorkorderTemplate] },
        loadChildren: () => import("./modules/admin/work-order-templates/work-order-templates.routes"),
      },
      {
        path: "labs",
        data: { permissions: [Permission.manageLabsPermission] },
        loadChildren: () => import("./modules/admin/laboratories/laboratories.routes"),
      },
      {
        path: "work-orders",
        data: { permissions: [Permission.viewWorkOrdersPermission] },
        loadChildren: () => import("./modules/testing/work-orders/work-orders.routes"),
      },
      {
        path: "audit-log",
        loadComponent: () => import("./modules/admin/audit-log/audit-log.component").then(m => m.AuditLogComponent),
      },
      {
        path: "clients",
        data: { permissions: [Permission.manageClientsPermission] },
        loadChildren: () => import("./modules/admin/clients/clients.routes"),
      },
      {
        path: "projects",
        data: { permissions: [Permission.manageProjectsPermission] },
        loadChildren: () => import("./modules/admin/projects/projects.routes"),
      },
      {
        path: "report-comments",
        loadChildren: () => import("./modules/admin/report-comments/report-comments.routes"),
      },
      {
        path: "report-client-contacts",
        loadChildren: () => import("./modules/admin/report-client-contacts/report-client-contacts.routes"),
      },
      {
        path: "road-state-authorities",
        data: {
          permissions: [Permission.manageRoadStateAuthoritiesPermission],
        },
        loadChildren: () => import("./modules/admin/road-state-authorities/road-state-authorities.routes"),
      },
      {
        path: "countries",
        data: { permissions: [Permission.manageCountryStatesPermission] },
        loadChildren: () => import("./modules/admin/countries-and-states/countries-and-states.routes"),
      },
      {
        path: "invoices",
        data: { permissions: [Permission.manageInvoices] },
        loadChildren: () => import("./modules/billing/invoices/invoices.routes"),
      },
      {
        path: "inventory",
        data: { permissions: [Permission.manageInventories] },
        loadChildren: () => import("./modules/billing/products-and-services/products-and-services.routes"),
      },
      {
        path: "data-export",
        data: { permissions: [Permission.manageMainRoadsDataCapture] },
        loadChildren: () => import("./modules/admin/data-export/data-export.routes"),
      },
      {
        path: "assigned-values",
        loadChildren: () => import("./modules/admin/assigned-values/assigned-values.routes"),
      },
      {
        path: "custom-worksheet-templates",
        data: { permissions: [Permission.viewCustomWorksheetTemplatesPermission] },
        loadChildren: () => import("./modules/admin/custom-worksheet-templates/custom-worksheet-templates.routes"),
      },
      {
        path: "settings",
        loadComponent: () => import("./modules/settings/settings.component").then(v => v.SettingsComponent),
        canActivate: [AuthGuard],
        children: [
          { path: "", redirectTo: "account", pathMatch: "full" },
          {
            path: "account",
            loadComponent: () => import("./modules/settings/account/account.component").then(m => m.AccountComponent),
          },
          {
            path: "users",
            data: { permissions: [Permission.viewUsersPermission] },
            loadComponent: () => import("./modules/settings/users/users.component").then(m => m.UsersComponent),
          },
          {
            path: "roles",
            data: { permissions: [Permission.viewRolesPermission] },
            loadComponent: () => import("./modules/settings/roles/roles.component").then(m => m.RolesComponent),
          },
          {
            path: "competency",
            loadComponent: () => import("./modules/settings/user-competency/user-competency.component").then(m => m.UserCompetencyComponent),
          },
          {
            path: "qualifications",
            loadComponent: () => import("./modules/settings/qualifications/qualifications.component").then(m => m.QualificationsComponent),
          },
          {
            path: "defaults",
            loadComponent: () => import("./modules/settings/defaults/defaults.component").then(m => m.DefaultsComponent),
          },
        ],
      },
      {
        path: "management-reports",
        loadComponent: () => import("./modules/management/management-reports/management-reports.component").then(m => m.ManagementReportsComponent),
        data: { permissions: [Permission.viewManagementReports] },
      },
      {
        path: "management-dashboards",
        loadComponent: () => import("./modules/management/management-dashboards/management-dashboards.component").then(m => m.ManagementDashboardsComponent),
        data: { permissions: [Permission.viewManagementReports] },
      },
      {
        path: "reports",
        loadComponent: () => import("./modules/testing/reports/reports.component").then(m => m.ReportsComponent),
      },
      {
        path: "concrete-worksheet",
        loadComponent: () => import("./modules/testing/concrete-worksheet/concrete-worksheet.component").then(m => m.ConcreteWorksheetComponent),
      },
      {
        path: "concrete-pickups",
        loadComponent: () => import("./modules/testing/concrete-pickups/concrete-pickups.component").then(mod => mod.ConcretePickupsComponent),
      },
      {
        path: "soil-summary",
        loadComponent: () => import("./modules/testing/soil-summary/soil-summary.component").then(v => v.SoilSummaryGridComponent),
      },
      {
        path: "sand-summary",
        loadComponent: () => import("./modules/testing/sand-summary/sand-summary.component").then(v => v.SandSummaryComponent),
      },
      {
        path: "quarry-products",
        loadComponent: () => import("./modules/testing/quarry-products-worksheet/quarry-products-worksheet.component").then(v => v.QuarryProductsWorksheetComponent),
      },
      {
        path: "concrete-worksheet-v1",
        loadComponent: () => import("./modules/testing/concrete-worksheet-v1/concrete-worksheet-v1.component").then(mod => mod.ConcreteWorksheetV1Component),
      },
      {
        path: "concrete-reporting",
        loadChildren: () => import("./modules/concrete/concrete-reporting/concrete-reporting.routes"),
        data: { permissions: [Permission.runConcreteReportingPermission], page: "Concrete Reporting" },
      },
      {
        path: "moisture-worksheet",
        loadComponent: () => import("./modules/testing/moisture-worksheet/moisture-worksheet.component").then(mod => mod.MoistureWorksheetComponent),
      },
      {
        path: "precast-lots",
        loadChildren: () => import("./modules/precast/precast-lots/precast-lots.routes"),
      },
      {
        path: "precast-operations",
        loadChildren: () => import("./modules/precast/precast-operations/precast-operations.routes"),
      },
      {
        path: "precast-moulds",
        loadComponent: () => import("./modules/precast/precast-moulds/precast-moulds.component").then(mod => mod.PrecastMouldsComponent),
      },
      {
        path: "precast-pipes",
        loadComponent: () => import("./modules/precast/precast-pipes/precast-pipes.component").then(mod => mod.PrecastPipesComponent),
      },
      {
        path: "precast-prestress",
        loadComponent: () => import("./modules/precast/precast-prestress/precast-prestress.component").then(mod => mod.PrecastPrestressComponent),
      },
      {
        path: "precast-areacodes",
        loadComponent: () => import("./modules/precast/precast-area-codes/precast-area-codes.component").then(mod => mod.PrecastAreaCodesComponent),
      },
      {
        path: "precast-work-types",
        loadComponent: () => import("./modules/precast/precast-work-types/precast-work-types.component").then(mod => mod.PrecastWorkTypesComponent),
      },
      {
        path: "precast-lot-tags",
        loadComponent: () => import("./modules/precast/precast-lot-tags/precast-lot-tags.component").then(mod => mod.PrecastLotTagsComponent),
      },
      {
        path: "precast-itps",
        loadComponent: () => import("./modules/precast/precast-itps/precast-itps.component").then(mod => mod.PrecastItpsComponent),
      },
      {
        path: "concrete-sample-strength",
        loadComponent: () =>
          import("./modules/test-methods/concrete/concrete-sample-strength-summary/concrete-sample-strength-summary.component").then(
            v => v.ConcreteSampleStrengthSummaryGridComponent
          ),
      },
      {
        path: "compressive-strength",
        loadComponent: () =>
          import("./modules/test-methods/concrete/concrete-strength-summary/concrete-strength-summary.component").then(v => v.ConcreteStrengthSummaryGridComponent),
      },
      {
        path: "chemical-register",
        loadChildren: () => import("./modules/admin/chemical-register/chemical-register-routes"),
      },
      {
        path: "feature-announcements",
        loadChildren: () => import("./layout/common/feature-announcements/feature-announcements.routes"),
      },
    ],
  },
];
